import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Pencil, Trash2, X, Building2, Inbox, Mail, Phone, Star, Upload, Settings, ChevronLeft, ChevronRight, Type } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { EDITABLE_KEYS, LANGUAGES } from '../context/LanguageContext';
import { translations } from '../i18n/translations';
import {
  fetchProperties,
  adminListEnquiries,
  adminCreateProperty,
  adminUpdateProperty,
  adminDeleteProperty,
  adminToggleFeatured,
  adminUploadImage,
  adminChangeCredentials,
  getSiteContent,
  adminSaveSiteContent,
} from '../data/api';
import Logo from '../components/Logo';
import { toast } from 'sonner';

const LANGS = ['fr', 'en', 'es', 'de', 'zh'];
const CATS = ['hotels', 'immeubles', 'appartements', 'exception', 'liquidation'];
const COUNTRIES = ['France', 'Suisse', 'International'];

const emptyML = () => ({ fr: '', en: '', es: '', de: '', zh: '' });

const emptyProperty = () => ({
  category: 'hotels',
  ref: '',
  country: 'France',
  featured: false,
  price: '',
  surface: '',
  title: emptyML(),
  location: emptyML(),
  excerpt: emptyML(),
  description: emptyML(),
  features: { fr: '', en: '', es: '', de: '', zh: '' }, // comma separated in form
  images: '', // newline separated in form
});

const toFormShape = (p) => ({
  category: p.category,
  ref: p.ref,
  country: p.country,
  featured: !!p.featured,
  price: p.price ?? '',
  surface: p.surface ?? '',
  title: { ...emptyML(), ...p.title },
  location: { ...emptyML(), ...p.location },
  excerpt: { ...emptyML(), ...p.excerpt },
  description: { ...emptyML(), ...p.description },
  features: LANGS.reduce((acc, l) => ({ ...acc, [l]: (p.features?.[l] || []).join(', ') }), {}),
  images: (p.images || []).join('\n'),
  id: p.id,
});

const toApiShape = (f) => ({
  id: f.id,
  category: f.category,
  ref: f.ref,
  country: f.country,
  featured: !!f.featured,
  price: f.price === '' ? null : parseFloat(f.price),
  surface: f.surface === '' ? null : parseFloat(f.surface),
  title: f.title,
  location: f.location,
  excerpt: f.excerpt,
  description: f.description,
  features: LANGS.reduce(
    (acc, l) => ({ ...acc, [l]: (f.features[l] || '').split(',').map((s) => s.trim()).filter(Boolean) }),
    {}
  ),
  images: (f.images || '').split('\n').map((s) => s.trim()).filter(Boolean),
});

const AdminDashboard = () => {
  const { token, logout } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadProps = useCallback(() => {
    fetchProperties().then(setProperties);
  }, []);

  const loadEnquiries = useCallback(() => {
    adminListEnquiries(token).then(setEnquiries).catch(() => {});
  }, [token]);

  useEffect(() => {
    loadProps();
    loadEnquiries();
  }, [loadProps, loadEnquiries]);

  const onLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const openNew = () => setEditing(emptyProperty());
  const openEdit = (p) => setEditing(toFormShape(p));

  const onDelete = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title?.fr || p.ref} » ?`)) return;
    try {
      await adminDeleteProperty(token, p.id);
      toast.success('Bien supprimé');
      loadProps();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const onToggleFeatured = async (p) => {
    try {
      await adminToggleFeatured(token, p.id, !p.featured);
      setProperties((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x)));
      toast.success(!p.featured ? 'Ajouté aux biens vedettes' : 'Retiré des vedettes');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = toApiShape(editing);
      if (editing.id) {
        await adminUpdateProperty(token, editing.id, payload);
        toast.success('Bien mis à jour');
      } else {
        await adminCreateProperty(token, payload);
        toast.success('Bien créé');
      }
      setEditing(null);
      loadProps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4efe6]">
      {/* top bar */}
      <header className="bg-[#1b2331] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo dark small />
            <span className="font-sans-elegant text-[0.62rem] tracking-brand uppercase text-[#c8a469]">
              Espace Admin
            </span>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#f4efe6]/70 hover:text-[#f4efe6] transition-colors"
            >
              <Settings className="w-4 h-4" />
              Identifiants
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#f4efe6]/70 hover:text-[#f4efe6] transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {/* tabs */}
        <div className="flex items-center gap-8 border-b border-[#1b2331]/10 mb-10">
          <button
            onClick={() => setTab('properties')}
            className={`flex items-center gap-2 pb-4 font-sans-elegant text-xs tracking-wide-2 uppercase transition-colors border-b-2 -mb-px ${
              tab === 'properties' ? 'text-[#1b2331] border-[#b0894f]' : 'text-[#1b2331]/50 border-transparent'
            }`}
          >
            <Building2 className="w-4 h-4" /> Biens ({properties.length})
          </button>
          <button
            onClick={() => setTab('enquiries')}
            className={`flex items-center gap-2 pb-4 font-sans-elegant text-xs tracking-wide-2 uppercase transition-colors border-b-2 -mb-px ${
              tab === 'enquiries' ? 'text-[#1b2331] border-[#b0894f]' : 'text-[#1b2331]/50 border-transparent'
            }`}
          >
            <Inbox className="w-4 h-4" /> Demandes ({enquiries.length})
          </button>
          <button
            onClick={() => setTab('texts')}
            className={`flex items-center gap-2 pb-4 font-sans-elegant text-xs tracking-wide-2 uppercase transition-colors border-b-2 -mb-px ${
              tab === 'texts' ? 'text-[#1b2331] border-[#b0894f]' : 'text-[#1b2331]/50 border-transparent'
            }`}
          >
            <Type className="w-4 h-4" /> Textes du site
          </button>
        </div>

        {tab === 'properties' && (
          <div>
            <div className="flex justify-end mb-6">
              <button
                onClick={openNew}
                className="flex items-center gap-2 bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase px-5 py-3 hover:bg-[#b0894f] transition-colors"
              >
                <Plus className="w-4 h-4" /> Ajouter un bien
              </button>
            </div>

            <div className="overflow-x-auto border border-[#1b2331]/10 bg-white/40">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[#1b2331]/10">
                    {['Réf.', 'Titre', 'Catégorie', 'Pays', 'Prix (€)', ''].map((h) => (
                      <th key={h} className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] px-4 py-3">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.id} className="border-b border-[#1b2331]/5 hover:bg-[#eae2d3]/40">
                      <td className="px-4 py-3 font-sans-elegant text-xs text-[#8a8d93]">{p.ref}</td>
                      <td className="px-4 py-3 font-serif-display text-lg text-[#1b2331]">{p.title?.fr}</td>
                      <td className="px-4 py-3 font-sans-elegant text-xs text-[#1b2331] capitalize">{p.category}</td>
                      <td className="px-4 py-3 font-sans-elegant text-xs text-[#1b2331]">{p.country}</td>
                      <td className="px-4 py-3 font-sans-elegant text-xs text-[#1b2331]">
                        {p.price ? p.price.toLocaleString('fr-FR') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => onToggleFeatured(p)}
                            className={`p-2 transition-colors ${p.featured ? 'text-[#b0894f]' : 'text-[#1b2331]/30 hover:text-[#b0894f]'}`}
                            title={p.featured ? 'Retirer des vedettes' : 'Mettre en vedette'}
                          >
                            <Star className="w-4 h-4" fill={p.featured ? '#b0894f' : 'none'} />
                          </button>
                          <button onClick={() => openEdit(p)} className="p-2 text-[#1b2331]/60 hover:text-[#b0894f] transition-colors" title="Modifier">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => onDelete(p)} className="p-2 text-[#1b2331]/60 hover:text-red-600 transition-colors" title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'enquiries' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {enquiries.length === 0 && (
              <p className="font-serif-display text-xl text-[#1b2331]/40 py-10">Aucune demande pour le moment.</p>
            )}
            {enquiries.map((e) => (
              <div key={e.id} className="border border-[#1b2331]/10 bg-white/40 p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-serif-display text-xl text-[#1b2331]">{e.name}</p>
                    <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mt-1">
                      {new Date(e.created_at).toLocaleString('fr-FR')} · {e.lang?.toUpperCase()}
                    </p>
                  </div>
                  {e.property_ref && (
                    <span className="bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-[0.58rem] tracking-wide-2 uppercase px-2.5 py-1">
                      {e.property_ref}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3">
                  <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 font-sans-elegant text-xs text-[#b0894f]">
                    <Mail className="w-3.5 h-3.5" /> {e.email}
                  </a>
                  {e.phone && (
                    <span className="flex items-center gap-1.5 font-sans-elegant text-xs text-[#1b2331]/70">
                      <Phone className="w-3.5 h-3.5" /> {e.phone}
                    </span>
                  )}
                </div>
                {e.subject && <p className="font-sans-elegant text-sm text-[#1b2331] mb-2">{e.subject}</p>}
                <p className="font-sans-elegant text-sm text-[#5a5d63] leading-relaxed">{e.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'texts' && <SiteTextsPanel token={token} />}
      </div>

      {/* Edit / Create drawer */}
      {editing && (
        <PropertyEditor
          editing={editing}
          setEditing={setEditing}
          onSave={onSave}
          saving={saving}
          token={token}
        />
      )}

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
};

const inputCls =
  'w-full bg-transparent border border-[#1b2331]/15 font-sans-elegant text-sm text-[#1b2331] px-3 py-2 focus:outline-none focus:border-[#b0894f] transition-colors';

const MLField = ({ label, value, onChange, textarea }) => (
  <div>
    <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">{label}</p>
    <div className="grid grid-cols-1 gap-2">
      {LANGS.map((l) => (
        <div key={l} className="flex items-start gap-2">
          <span className="font-sans-elegant text-[0.62rem] uppercase text-[#b0894f] w-7 pt-2 shrink-0">{l}</span>
          {textarea ? (
            <textarea
              value={value[l]}
              onChange={(e) => onChange(l, e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
            />
          ) : (
            <input value={value[l]} onChange={(e) => onChange(l, e.target.value)} className={inputCls} />
          )}
        </div>
      ))}
    </div>
  </div>
);

const PropertyEditor = ({ editing, setEditing, onSave, saving, token }) => {
  const [uploading, setUploading] = useState(false);
  const setML = (key) => (lang, v) =>
    setEditing((prev) => ({ ...prev, [key]: { ...prev[key], [lang]: v } }));
  const setField = (key, v) => setEditing((prev) => ({ ...prev, [key]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUploadImage(token, file);
      setEditing((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${url}` : url }));
      toast.success('Image ajoutée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de l’envoi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[#1b2331]/50" onClick={() => setEditing(null)} />
      <div className="relative w-full max-w-2xl bg-[#f4efe6] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#1b2331] px-8 py-5 flex items-center justify-between z-10">
          <h2 className="font-serif-display text-2xl text-[#f4efe6]">
            {editing.id ? 'Modifier le bien' : 'Nouveau bien'}
          </h2>
          <button onClick={() => setEditing(null)} className="text-[#f4efe6]/70 hover:text-[#f4efe6]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 space-y-7">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">Référence</p>
              <input value={editing.ref} onChange={(e) => setField('ref', e.target.value)} className={inputCls} />
            </div>
            <div>
              <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">Catégorie</p>
              <select value={editing.category} onChange={(e) => setField('category', e.target.value)} className={inputCls}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">Pays</p>
              <select value={editing.country} onChange={(e) => setField('country', e.target.value)} className={inputCls}>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">Prix (€)</p>
                <input type="number" value={editing.price} onChange={(e) => setField('price', e.target.value)} className={inputCls} />
              </div>
              <div>
                <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">Surface (m²)</p>
                <input type="number" value={editing.surface} onChange={(e) => setField('surface', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!editing.featured}
              onChange={(e) => setField('featured', e.target.checked)}
              className="w-4 h-4 accent-[#b0894f]"
            />
            <span className="flex items-center gap-2 font-sans-elegant text-sm text-[#1b2331]">
              <Star className="w-4 h-4 text-[#b0894f]" fill={editing.featured ? '#b0894f' : 'none'} />
              Mettre ce bien en vedette (page d’accueil)
            </span>
          </label>

          <MLField label="Titre" value={editing.title} onChange={setML('title')} />
          <MLField label="Localisation" value={editing.location} onChange={setML('location')} />
          <MLField label="Accroche" value={editing.excerpt} onChange={setML('excerpt')} textarea />
          <MLField label="Description" value={editing.description} onChange={setML('description')} textarea />
          <MLField label="Caractéristiques (séparées par des virgules)" value={editing.features} onChange={setML('features')} />

          <ImageGallery
            images={(editing.images || '').split('\n').map((s) => s.trim()).filter(Boolean)}
            onChange={(arr) => setField('images', arr.join('\n'))}
            onUpload={onUpload}
            uploading={uploading}
          />

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={onSave}
              disabled={saving}
              className="bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-8 py-3.5 hover:bg-[#b0894f] transition-colors disabled:opacity-60"
            >
              {saving ? '…' : 'Enregistrer'}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="font-sans-elegant text-xs tracking-wide-2 uppercase text-[#1b2331]/60 hover:text-[#1b2331] px-4"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageGallery = ({ images, onChange, onUpload, uploading }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const move = (from, to) => {
    if (to < 0 || to >= images.length || from === to) return;
    const arr = [...images];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onChange(arr);
  };

  const setMain = (i) => move(i, 0);
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIndex !== null) move(dragIndex, i);
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93]">
          Galerie photos — glissez pour réordonner
        </p>
        <label className={`flex items-center gap-2 font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase cursor-pointer transition-colors ${uploading ? 'text-[#8a8d93]' : 'text-[#b0894f] hover:text-[#1b2331]'}`}>
          <Upload className="w-3.5 h-3.5" />
          {uploading ? 'Envoi…' : 'Importer une photo'}
          <input type="file" accept="image/*" onChange={onUpload} disabled={uploading} className="hidden" />
        </label>
      </div>

      {images.length === 0 ? (
        <p className="font-sans-elegant text-sm text-[#8a8d93] border border-dashed border-[#1b2331]/20 py-8 text-center">
          Aucune photo. Importez une photo ou ajoutez une URL ci-dessous.
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
          {images.map((u, i) => (
            <div
              key={`${u.slice(0, 24)}-${i}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragEnter={() => setOverIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className={`group relative aspect-square overflow-hidden border cursor-grab active:cursor-grabbing transition-all ${
                overIndex === i && dragIndex !== null ? 'border-[#b0894f] ring-2 ring-[#b0894f]/40' : 'border-[#1b2331]/15'
              } ${dragIndex === i ? 'opacity-40' : ''}`}
            >
              <img src={u} alt="" className="w-full h-full object-cover pointer-events-none" />
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-[#b0894f] text-[#f4efe6] font-sans-elegant text-[0.52rem] tracking-wide-2 uppercase px-1.5 py-0.5">
                  Principale
                </span>
              )}
              <div className="absolute inset-0 bg-[#1b2331]/0 group-hover:bg-[#1b2331]/55 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => setMain(i)}
                    title="Définir comme principale"
                    className="bg-[#f4efe6] text-[#1b2331] p-1.5 hover:bg-[#b0894f] hover:text-[#f4efe6] transition-colors"
                  >
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(i)}
                  title="Supprimer"
                  className="bg-[#f4efe6] text-[#1b2331] p-1.5 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100">
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} title="Reculer" className="bg-[#f4efe6]/90 text-[#1b2331] p-1 disabled:opacity-30 hover:bg-[#b0894f] hover:text-[#f4efe6] transition-colors">
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === images.length - 1} title="Avancer" className="bg-[#f4efe6]/90 text-[#1b2331] p-1 disabled:opacity-30 hover:bg-[#b0894f] hover:text-[#f4efe6] transition-colors">
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <details className="mt-1">
        <summary className="font-sans-elegant text-[0.6rem] tracking-wide-2 uppercase text-[#8a8d93] cursor-pointer hover:text-[#1b2331]">
          Ajouter / éditer par URL
        </summary>
        <textarea
          value={images.join('\n')}
          onChange={(e) => onChange(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
          rows={3}
          placeholder="Une URL par ligne"
          className={`${inputCls} resize-none mt-2`}
        />
      </details>
    </div>
  );
};

const SiteTextsPanel = ({ token }) => {
  const [values, setValues] = useState(null); // { key: { fr,en,es,de,zh } }
  const [saving, setSaving] = useState(false);

  const defaultFor = (key, lang) => {
    const parts = key.split('.');
    let node = translations[lang];
    for (const p of parts) node = node?.[p];
    return typeof node === 'string' ? node : '';
  };

  useEffect(() => {
    getSiteContent()
      .then((data) => {
        const ov = data?.overrides || {};
        const init = {};
        EDITABLE_KEYS.forEach(({ key }) => {
          init[key] = {};
          LANGUAGES.forEach(({ code }) => {
            init[key][code] = ov[key]?.[code] ?? '';
          });
        });
        setValues(init);
      })
      .catch(() => setValues({}));
  }, []);

  const onChange = (key, lang, v) =>
    setValues((prev) => ({ ...prev, [key]: { ...prev[key], [lang]: v } }));

  const onSave = async () => {
    setSaving(true);
    try {
      // only keep non-empty overrides
      const overrides = {};
      Object.entries(values).forEach(([key, langs]) => {
        const kept = {};
        Object.entries(langs).forEach(([l, v]) => { if (v && v.trim()) kept[l] = v.trim(); });
        if (Object.keys(kept).length) overrides[key] = kept;
      });
      await adminSaveSiteContent(token, overrides);
      toast.success('Textes enregistrés — visibles après rechargement du site');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <p className="font-sans-elegant text-sm text-[#8a8d93]">Chargement…</p>;

  return (
    <div className="max-w-4xl">
      <p className="font-sans-elegant text-sm text-[#5a5d63] leading-relaxed mb-8 border-l-2 border-[#b0894f] pl-4">
        Modifiez les titres et slogans du site dans les 5 langues. Laissez un champ vide pour conserver le texte par défaut (affiché en filigrane). Les changements sont <strong>enregistrés en direct</strong> — un simple rechargement du site suffit, sans republier.
      </p>

      <div className="space-y-8">
        {EDITABLE_KEYS.map(({ key, label }) => (
          <div key={key} className="border border-[#1b2331]/10 bg-white/40 p-6">
            <p className="font-serif-display text-xl text-[#1b2331] mb-1">{label}</p>
            <p className="font-sans-elegant text-[0.6rem] tracking-wide-2 uppercase text-[#8a8d93] mb-4">{key}</p>
            <div className="space-y-2.5">
              {LANGUAGES.map(({ code, short }) => (
                <div key={code} className="flex items-start gap-2">
                  <span className="font-sans-elegant text-[0.62rem] uppercase text-[#b0894f] w-8 pt-2.5 shrink-0">{short}</span>
                  <textarea
                    value={values[key][code]}
                    onChange={(e) => onChange(key, code, e.target.value)}
                    placeholder={defaultFor(key, code)}
                    rows={2}
                    className="w-full bg-transparent border border-[#1b2331]/15 font-sans-elegant text-sm text-[#1b2331] px-3 py-2 focus:outline-none focus:border-[#b0894f] transition-colors resize-none placeholder:text-[#1b2331]/35"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 bg-[#f4efe6] py-5 mt-4 border-t border-[#1b2331]/10">
        <button
          onClick={onSave}
          disabled={saving}
          className="bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-10 py-4 hover:bg-[#b0894f] transition-colors disabled:opacity-60"
        >
          {saving ? '…' : 'Enregistrer les textes'}
        </button>
      </div>
    </div>
  );
};

const SettingsModal = ({ onClose }) => {
  const { token, login } = useAdmin();
  const [form, setForm] = useState({ new_username: '', current_password: '', new_password: '' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast.error('Veuillez saisir votre mot de passe actuel');
      return;
    }
    setSaving(true);
    try {
      const data = await adminChangeCredentials(token, {
        new_username: form.new_username || null,
        current_password: form.current_password,
        new_password: form.new_password || null,
      });
      login(data.token); // refresh token with (possibly) new username
      toast.success('Identifiants mis à jour');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const cls =
    'w-full bg-transparent border border-[#1b2331]/15 font-sans-elegant text-sm text-[#1b2331] px-3 py-2.5 focus:outline-none focus:border-[#b0894f] transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      <div className="absolute inset-0 bg-[#1b2331]/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#f4efe6] shadow-2xl">
        <div className="bg-[#1b2331] px-8 py-5 flex items-center justify-between">
          <h2 className="font-serif-display text-2xl text-[#f4efe6]">Modifier mes identifiants</h2>
          <button onClick={onClose} className="text-[#f4efe6]/70 hover:text-[#f4efe6]">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-8 space-y-6">
          <div>
            <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">
              Nouvel identifiant (optionnel)
            </p>
            <input value={form.new_username} onChange={(e) => set('new_username', e.target.value)} className={cls} placeholder="Laisser vide pour ne pas changer" autoComplete="off" />
          </div>
          <div>
            <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">
              Nouveau mot de passe (optionnel)
            </p>
            <input type="password" value={form.new_password} onChange={(e) => set('new_password', e.target.value)} className={cls} placeholder="Laisser vide pour ne pas changer" autoComplete="new-password" />
          </div>
          <div className="pt-2 border-t border-[#1b2331]/10">
            <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#b0894f] mb-2">
              Mot de passe actuel (requis)
            </p>
            <input type="password" value={form.current_password} onChange={(e) => set('current_password', e.target.value)} required className={cls} autoComplete="current-password" />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving} className="bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-8 py-3.5 hover:bg-[#b0894f] transition-colors disabled:opacity-60">
              {saving ? '…' : 'Enregistrer'}
            </button>
            <button type="button" onClick={onClose} className="font-sans-elegant text-xs tracking-wide-2 uppercase text-[#1b2331]/60 hover:text-[#1b2331] px-4">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
